import * as React from 'react';
import classnames from 'classnames';
import Flex, { Props as FlexProps } from '../Flex/Flex';
import Icon from '../Icon/Icon';
import { TypeIconName } from '../Icon/tsx';
import Link from '../Link/Link';
import ProgressSpinner from '../Progress/ProgressSpinner';
import './Notification.css';

type TypeNotificationType =
  'alert' |
  'inform' |
  'progress' |
  'success' |
  'warning';

export interface Props extends FlexProps {
  /**
   * A callback to remove the notification, that when provided will
   * render a clickable cross, that when clicked will call the given
   * function.
   */
  onRemove?: (e: React.SyntheticEvent) => void;
  /**
   * Name of one of the Bitkit icons that will accompany the message
   * of the notification. By default, some of the notification
   * types have icons, but these can be overridden.
   */
  icon?: TypeIconName;
  /**
   * The type of the notification that affects the styling and content.
   */
  type: TypeNotificationType;
}

const defaultIcons: {
  [key: string]: TypeIconName;
} = {
  alert: 'ErrorGeneral',
  success: 'Tick',
  warning: 'Warning',
};

/**
 * Notification component used to inform the user of something important.
 */
const Notification: React.FunctionComponent<Props> = (props: Props) => {
  const {
    children,
    onRemove,
    type,
    icon,
    ...rest
  } = props;

  const classes = classnames('Notification', `Notification--type-${type}`);
  const defaultIcon = defaultIcons[type];

  return (
    <Flex { ...rest }
        alignChildrenVertical="middle"
        className={ classes }
        direction="horizontal"
        gap="x2"
        padding="x3">
      { (icon || defaultIcon || type === 'progress') && (
        <Flex>
          { type === 'progress'
            ? <ProgressSpinner />
            : <Icon name={ icon || defaultIcon } /> }
        </Flex>
      ) }

      <Flex grow shrink>
        { children }
      </Flex>

      { onRemove && (
        <Flex>
          <Link clickable onClick={ onRemove }>
            <Icon name="CloseSmall" />
          </Link>
        </Flex>
      ) }
    </Flex>
  );
};

export default Notification;
